import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUsers } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const JobList = () => {
  const navigate = useNavigate();
  const toast = UseToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicantCounts, setApplicantCounts] = useState({});

  useEffect(() => {
    fetchJobs();
    fetchApplicantCounts();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/jobs`);
      const data = await res.json();
      
      if (data.success) {
        setJobs(data.data || []);
      } else {
        toast.error('Failed to load jobs');
        setJobs([]);
      }
    } catch (error) {
      toast.error('Server error');
      console.error('Fetch error:', error);
      setJobs([]);
    }
    setLoading(false);
  };

  const fetchApplicantCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        const counts = {};
        data.data.forEach(app => {
          const jobId = app.jobId._id || app.jobId;
          counts[jobId] = (counts[jobId] || 0) + 1;
        });
        setApplicantCounts(counts);
      }
    } catch (error) {
      console.error('Fetch applicants error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/jobs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Job deleted successfully');
        setJobs(jobs.filter(j => j._id!== id));
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Delete error:', error);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive? 
      { text: 'Active', class: 'status-active' } : 
      { text: 'Closed', class: 'status-closed' };
  };

  return (
    <>
      <style>{`
  .page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 30px; }
  .page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0; }
  .table-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th { text-align: left; padding: 12px; font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; border-bottom: 2px solid #E2E8F0; }
  .data-table td { padding: 16px 12px; border-bottom: 1px solid #F1F5F9; font-size: 14px; }
  .data-table tr:hover { background: #F8FAFC; }
  .action-btns { display: flex; gap: 8px; }
  .icon-btn { width: 36px; height: 36px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s ease; }
  .icon-btn.edit { background: #DBEAFE; color: #1E40AF; }
  .icon-btn.edit:hover { background: #3B82F6; color: #FFF; transform: translateY(-2px); }
  .icon-btn.delete { background: #FEE2E2; color: #991B1B; }
  .icon-btn.delete:hover { background: #EF4444; color: #FFF; transform: translateY(-2px); }
  .icon-btn.view { background: #D1FAE5; color: #065F46; }
  .icon-btn.view:hover { background: #10B981; color: #FFF; transform: translateY(-2px); }
  .badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-block; }
  .badge-type { background: #D1FAE5; color: #065F46; }
  .status-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-block; }
  .status-active { background: #D1FAE5; color: #065F46; }
  .status-closed { background: #F3F4F6; color: #6B7280; }
  .applicant-count { display: flex; align-items: center; gap: 6px; color: #64748B; font-weight: 600; }
  .loading-state,.empty-state { text-align: center; padding: 60px 20px; color: #64748B; }
  .empty-icon { font-size: 64px; margin-bottom: 16px; opacity: 0.5; }
        @media (max-width: 768px) {
       .page-header { flex-direction: column; align-items: flex-start; gap: 16px; }
       .table-card { padding: 16px; overflow-x: auto; }
        }
      `}</style>
      <div>
        <div className="page-header">
          <h1 className="page-title">Job Openings</h1>
          <Button onClick={() => navigate('/careers/jobs/add')} icon={<FiPlus />}>
            Post New Job
          </Button>
        </div>
        <div className="table-card">
          {loading? (
            <div className="loading-state">Loading jobs...</div>
          ) : jobs.length === 0? (
            <div className="empty-state">
              <div className="empty-icon">💼</div>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A' }}>No jobs yet</p>
              <p style={{ fontSize: '14px', color: '#64748B' }}>Create your first job posting</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Applicants</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const status = getStatusBadge(job.isActive);
                  return (
                    <tr key={job._id}>
                      <td><strong>{job.title}</strong></td>
                      <td>{job.location}</td>
                      <td><span className="badge badge-type">{job.type}</span></td>
                      <td><span className={`status-badge ${status.class}`}>{status.text}</span></td>
                      <td>
                        <div className="applicant-count">
                          <FiUsers /> {applicantCounts[job._id] || 0}
                        </div>
                      </td>
                      <td>
                        <div className="action-btns">
                          <button 
                            className="icon-btn view" 
                            onClick={() => navigate(`/careers/applications?jobId=${job._id}`)}
                            title="View Applications"
                          >
                            <FiUsers />
                          </button>
                          <button 
                            className="icon-btn edit" 
                            onClick={() => navigate(`/careers/jobs/edit/${job._id}`)}
                          >
                            <FiEdit2 />
                          </button>
                          <button 
                            className="icon-btn delete" 
                            onClick={() => handleDelete(job._id)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default JobList;